import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';
import type { HushPropTemplate } from '../../../../shared/scene/hushPropTemplates';

/**
 * Folded paper boat (64 × 40 design units).
 * Clean origami geometry: far fold/sail, hull, near keel fold, and contact shadow.
 */
export function buildPaperBoat(p: TownPropPalette): string {
  const iron = hex(p.iron);
  const shade = hex(p.shade);
  const paper = hex(p.primary);
  const paperLight = hex(p.glassCore);

  return `<g data-part="hush-paper-boat">
    <!-- Contact shadow -->
    <ellipse cx="32" cy="38" rx="24" ry="2" fill="${iron}" opacity="0.2" />
    <!-- Far sail fold -->
    <polygon points="18,22 32,5 46,22" fill="${paperLight}" stroke="${iron}" stroke-width="1.2" stroke-linejoin="round" />
    <!-- Main hull -->
    <polygon points="4,22 22,36 42,36 60,22" fill="${paper}" stroke="${iron}" stroke-width="1.4" stroke-linejoin="round" />
    <!-- Near keel facet shadow -->
    <polygon points="4,22 32,28 60,22 42,36 22,36" fill="${shade}" opacity="0.35" />
    <!-- Central fold ridge -->
    <path d="M32 5 L32 28" fill="none" stroke="${iron}" stroke-width="0.8" opacity="0.6" />
    <!-- Pip clue tab with subtle motif dots -->
    <circle cx="30" cy="24" r="1.4" fill="${iron}" opacity="0.7" />
    <circle cx="34" cy="24" r="1.4" fill="${iron}" opacity="0.7" />
    <circle cx="32" cy="28" r="1.4" fill="${iron}" opacity="0.7" />
  </g>`;
}

/**
 * Chalk skip marks (200 × 32 design units).
 * Three subtle dashed skipping arcs showing hop trajectory across flat practice stone.
 */
export function buildChalkSkipMarks(p: TownPropPalette): string {
  const chalk = hex(p.glassCore);

  return `<g data-part="hush-chalk-skip-marks" opacity="0.38">
    <!-- Arc 1 -->
    <path d="M10 28 Q40 4 70 28" fill="none" stroke="${chalk}" stroke-width="2.2" stroke-linecap="round" stroke-dasharray="10 6 6 4" />
    <ellipse cx="68" cy="27" rx="3.5" ry="1.5" fill="${chalk}" />
    <!-- Arc 2 -->
    <path d="M72 28 Q105 4 138 28" fill="none" stroke="${chalk}" stroke-width="2.2" stroke-linecap="round" stroke-dasharray="10 6 6 4" />
    <ellipse cx="136" cy="27" rx="3.5" ry="1.5" fill="${chalk}" />
    <!-- Arc 3 -->
    <path d="M140 28 Q168 6 195 28" fill="none" stroke="${chalk}" stroke-width="2.2" stroke-linecap="round" stroke-dasharray="8 5 6 4" />
    <ellipse cx="193" cy="27" rx="3.5" ry="1.5" fill="${chalk}" />
  </g>`;
}

/** Named template dispatch within the town template prop factory. */
/**
 * Keepsake wall shelf (120 × 56 design units, backdrop-mounted).
 * Mallow's quiet-edge shelf: one plank on two iron brackets holding a shallow
 * stone water basin and a small stack of smooth collected stones. Painted
 * art only; it grants no collision or support to other props.
 */
export function buildKeepsakeShelf(p: TownPropPalette): string {
  const iron = hex(p.iron);
  const wood = hex(p.wood);
  const woodEdge = hex(p.woodEdge);
  const stone = hex(p.stone);
  const shade = hex(p.shade);
  const water = hex(p.roof);
  const waterLight = hex(p.roofEdge);
  const warm = hex(p.glassCore);

  return `<g data-part="hush-keepsake-shelf">
    <!-- Iron brackets into the wall -->
    <path d="M18 40 V54 L30 40Z" fill="${iron}" opacity="0.85" />
    <path d="M102 40 V54 L90 40Z" fill="${iron}" opacity="0.85" />
    <!-- Plank with warm lit top edge -->
    <rect x="4" y="34" width="112" height="7" rx="1.5" fill="${wood}" stroke="${iron}" stroke-width="1.2" />
    <path d="M6 35 H114" stroke="${woodEdge}" stroke-width="1.2" stroke-linecap="round" />
    <path d="M40 37 h10 M76 38 h8" stroke="${shade}" stroke-width="0.8" opacity="0.7" />
    <!-- Shallow stone basin with still water -->
    <path d="M14 34 C14 26 20 22 34 22 C48 22 54 26 54 34Z" fill="${stone}" stroke="${iron}" stroke-width="1.2" stroke-linejoin="round" />
    <ellipse cx="34" cy="24" rx="17" ry="3.2" fill="${water}" stroke="${iron}" stroke-width="0.9" />
    <path d="M24 23.6 q6 -1.4 12 0" fill="none" stroke="${waterLight}" stroke-width="1" stroke-linecap="round" />
    <path d="M20 30 q14 3 28 0" fill="none" stroke="${shade}" stroke-width="0.8" opacity="0.6" />
    <!-- Smooth collected stones, largest at the bottom -->
    <ellipse cx="84" cy="30" rx="13" ry="4.4" fill="${stone}" stroke="${iron}" stroke-width="1.1" />
    <ellipse cx="82" cy="23.5" rx="9" ry="3.6" fill="${woodEdge}" stroke="${iron}" stroke-width="1" />
    <ellipse cx="84" cy="18" rx="5.5" ry="2.8" fill="${waterLight}" stroke="${iron}" stroke-width="0.9" />
    <path d="M78 28.6 q5 -1.6 10 0 M78 22.4 q3 -1 6 0" fill="none" stroke="${warm}" stroke-width="0.8" opacity="0.8" />
    <!-- A single small paper fold leaning at the end -->
    <path d="M100 34 L106 22 L112 34Z" fill="${warm}" stroke="${iron}" stroke-width="0.9" stroke-linejoin="round" />
  </g>`;
}

/** Templates still drawn here; lanterns/ribbon/reeds/brace (hushStatic) and window/cushion/cups
 * (hushDomestic) come from the reviewed recipe generators. */
export type HushHandDrawnTemplate = Extract<HushPropTemplate,
  'paper_boat' | 'chalk_skip_marks' | 'keepsake_shelf'>;

export function buildHushPropBody(template: HushHandDrawnTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'paper_boat': return buildPaperBoat(palette);
  case 'chalk_skip_marks': return buildChalkSkipMarks(palette);
  case 'keepsake_shelf': return buildKeepsakeShelf(palette);
  }
}
